import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import Lightbox, { resolveUrl } from "@/components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientGallery = () => {
  const { slug } = useParams();
  const [client, setClient] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightIdx, setLightIdx] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/clients/${slug}`);
        if (!cancel) setClient(data);
      } catch {
        if (!cancel) setNotFound(true);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  if (notFound) {
    return (
      <section className="pt-40 pb-24 min-h-screen">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)]">404</div>
          <h1 className="mt-4 font-display text-5xl text-white">Client not found.</h1>
          <Link to="/clients" className="btn-gold mt-10 inline-flex"><span>Back to Clients</span></Link>
        </div>
      </section>
    );
  }

  if (!client) {
    return (
      <section className="pt-40 pb-24 min-h-screen">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 font-mono-tech text-white/40 text-sm">Loading gallery…</div>
      </section>
    );
  }

  const photos = client.photos || [];

  return (
    <>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 border-b border-white/5 overflow-hidden" data-testid="client-hero">
        {photos[0] && (
          <div className="absolute inset-0 opacity-30">
            <img src={resolveUrl(photos[0].url)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
          </div>
        )}
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/clients" className="inline-flex items-center gap-2 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-8">
              <ArrowLeft size={14} /> All Clients
            </Link>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
              <div>
                <img
                  src={resolveUrl(client.logo_url)}
                  alt={client.name}
                  className="h-24 md:h-32 w-auto object-contain mb-8"
                  style={{ filter: "drop-shadow(0 0 22px rgba(212,175,55,0.15))" }}
                />
                <h1 className="font-display text-5xl md:text-8xl leading-[0.92] tracking-[-0.03em] text-white">
                  {client.name}
                </h1>
                {client.location && (
                  <div className="mt-5 flex items-center gap-2 font-mono-tech text-xs tracking-[0.22em] uppercase text-white/65">
                    <MapPin size={14} className="text-[var(--gold)]" /> {client.location}
                  </div>
                )}
              </div>
              <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/45">
                {photos.length} {photos.length === 1 ? "Frame" : "Frames"} · Captured by Lucra
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          {photos.length === 0 ? (
            <div className="text-white/55" data-testid="client-empty">No photos in this gallery yet.</div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-4 [column-fill:_balance]" data-testid="client-gallery-grid">
              {photos.map((p, i) => (
                <button
                  key={p.id || i}
                  onClick={() => setLightIdx(i)}
                  className="group relative block w-full mb-3 md:mb-4 overflow-hidden bg-[#0a0a0a] break-inside-avoid"
                  data-testid={`client-photo-${i}`}
                >
                  <img
                    src={resolveUrl(p.url)}
                    alt={p.caption || client.name}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/80">
                      Frame · {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 border-t border-white/5">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <h3 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-2xl">
            Book Lucra for your <span className="font-serif-display text-[var(--gold)]">next night</span>.
          </h3>
          <Link to="/contact" className="btn-gold" data-testid="client-cta"><span>Inquire</span><ArrowUpRight size={14} /></Link>
        </div>
      </section>

      <Lightbox photos={photos} index={lightIdx} onClose={() => setLightIdx(null)} onIndex={setLightIdx} />
    </>
  );
};

export default ClientGallery;
